import React, { useEffect, useRef, useState } from "react";
import {
  Globe, ChevronDown, ChevronUp, ExternalLink, Check, Loader2, Search,
  AlertCircle, RotateCw,
} from "lucide-react";
import http from "@/lib/http";

/**
 * KlapperPanel — automatic deklapper.be lookup + manual override.
 *
 * Flow:
 *   1. When a lead reaches a status whose target_view = parkeren_zoeken,
 *      the backend auto-creates an empty grid (1 main + 10 neighbors,
 *      ±5 same parity) and kicks off an async scrape against deklapper.be.
 *   2. While the scrape is running, the panel polls every 2.5s and renders
 *      a live progress line ("Buren opzoeken (4 / 10)").
 *   3. Each row that comes back with a hit is auto-filled (name, phone,
 *      sometimes email). Rows without a hit stay empty.
 *   4. The user can override / annotate any row manually — manual edits
 *      are persisted with `source: "Manueel"` and **never** overwritten on
 *      re-scrape.
 *   5. The verified-owner checkbox stays manual; auto-fetched data is
 *      kept separate from `lead.owner_*` fields.
 */
export default function KlapperPanel({ lead, onChanged }) {
  const [klapperOpen, setKlapperOpen] = useState(true);
  const [googleOpen, setGoogleOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const status = lead?.klapper_status || "idle";
  const isScraping = status === "running" || status === "pending";
  const results = lead?.klapper_results || {};
  const main = results.main || null;
  const neighbors = Array.isArray(results.neighbors) ? results.neighbors : [];
  const hitsCount = (main?.found ? 1 : 0) + neighbors.filter(n => n.found).length;
  const finishedAt = lead?.klapper_finished_at
    ? new Date(lead.klapper_finished_at).toLocaleString("nl-BE")
    : null;

  // Live polling while scrape is in flight
  useEffect(() => {
    if (isScraping) {
      if (!pollRef.current) {
        pollRef.current = setInterval(() => onChanged?.(), 2500);
      }
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [isScraping, onChanged]);

  const initScrape = async () => {
    setBusy(true);
    try {
      await http.post(`/api/leads/${lead.id}/klapper/init`);
      onChanged?.();
    } finally { setBusy(false); }
  };
  const rescrape = async () => {
    setBusy(true);
    try {
      await http.post(`/api/leads/${lead.id}/klapper/rescrape`);
      onChanged?.();
    } finally { setBusy(false); }
  };

  const updateMain = async (patch) => {
    await http.patch(`/api/leads/${lead.id}/klapper/main`, patch);
    onChanged?.();
  };
  const updateNeighbor = async (idx, patch) => {
    await http.patch(`/api/leads/${lead.id}/klapper/neighbors/${idx}`, patch);
    onChanged?.();
  };

  const klapperLookupUrl = (addr) => addr
    ? `https://www.deklapper.be/?q=${encodeURIComponent(addr)}`
    : "https://www.deklapper.be";

  return (
    <div className="mt-8 space-y-4" data-testid="klapper-panel">
      <section className="card !p-0 overflow-hidden">
        <header className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-[var(--border-soft)]">
          <button type="button" onClick={() => setKlapperOpen(v => !v)}
                  className="flex items-start gap-2 text-left flex-1">
            {klapperOpen ? <ChevronUp size={16} className="mt-1 text-[var(--ink-400)]" />
                         : <ChevronDown size={16} className="mt-1 text-[var(--ink-400)]" />}
            <div>
              <h3 className="font-display text-[20px] leading-tight">De Klapper</h3>
              <p className="text-[12px] text-[var(--ink-500)] mt-0.5"
                 data-testid="klapper-subline">
                {isScraping ? (
                  <span className="inline-flex items-center gap-1.5 text-[var(--teal-700)] font-medium">
                    <Loader2 size={12} className="animate-spin" />
                    {lead.klapper_progress || "Bezig met zoeken op deklapper.be…"}
                  </span>
                ) : status === "failed" ? (
                  <span className="inline-flex items-center gap-1.5 text-[var(--danger,#D6646E)]">
                    <AlertCircle size={12} /> Scrape mislukt: {lead.klapper_error || "onbekende fout"}
                  </span>
                ) : status === "done" ? (
                  <span>
                    {hitsCount} {hitsCount === 1 ? "rij" : "rijen"} ingevuld
                    {finishedAt && ` · ${finishedAt}`}
                  </span>
                ) : status === "ready" ? (
                  <span>Raster klaar — klik "Zoek automatisch" om deklapper.be te raadplegen</span>
                ) : (
                  <span>Nog niet gezocht — verschuif de lead naar "Zoeken" voor automatische opzoeking</span>
                )}
              </p>
            </div>
          </button>
          <div className="flex items-center gap-2 shrink-0">
            <a href={klapperLookupUrl(lead.address)} target="_blank" rel="noreferrer"
               className="text-[12px] text-[#B8924A] hover:underline flex items-center gap-1"
               data-testid="klapper-source-link">
              <Globe size={12} /> deklapper.be
            </a>
            {!isScraping && status !== "done" && (
              <button type="button"
                      data-testid="klapper-init"
                      disabled={busy}
                      onClick={initScrape}
                      className="btn !h-9 !px-3 !text-[12px] bg-[#E5C9A0] hover:bg-[#D8B883] text-[#5A3F0B] border-transparent disabled:opacity-50">
                <Search size={13} /> Zoek automatisch
              </button>
            )}
            {!isScraping && status === "done" && (
              <button type="button"
                      data-testid="klapper-rescrape"
                      disabled={busy}
                      onClick={rescrape}
                      className="btn !h-9 !px-3 !text-[12px] bg-white hover:bg-[var(--sand-50)] text-[var(--ink-700)] disabled:opacity-50">
                <RotateCw size={13} /> Opnieuw zoeken
              </button>
            )}
            {isScraping && (
              <span className="btn !h-9 !px-3 !text-[12px] bg-[#E5C9A0] text-[#5A3F0B] border-transparent">
                <Loader2 size={13} className="animate-spin" /> Zoeken…
              </span>
            )}
          </div>
        </header>

        {klapperOpen && (
          <div className="px-5 py-5 space-y-6">
            {status === "idle" || (!main && neighbors.length === 0) ? (
              <div className="text-[13px] italic text-[var(--ink-400)]">
                Nog geen raster aangemaakt. Verschuif deze lead naar status
                "Zoeken" of klik op "Zoek automatisch" rechtsboven.
              </div>
            ) : (
              <>
                {/* Main address row */}
                <div data-testid="klapper-main">
                  <div className="text-[12px] text-[var(--ink-500)] mb-2 flex items-center justify-between">
                    <span>Hoofdadres — {main?.address || lead.address}</span>
                    <a href={klapperLookupUrl(main?.address || lead.address)}
                       target="_blank" rel="noreferrer"
                       className="text-[11px] text-[#B8924A] hover:underline flex items-center gap-1">
                      Open op deklapper.be <ExternalLink size={11} />
                    </a>
                  </div>
                  <MainRow main={main || { address: lead.address }} onSave={updateMain} />
                </div>

                {/* Neighbor grid */}
                <div>
                  <div className="text-[12px] text-[var(--ink-500)] mb-3 flex items-center justify-between">
                    <span>
                      Buren ({neighbors.length}) — automatisch opgezocht;
                      pas aan waar nodig
                    </span>
                    {(() => {
                      const filledN = neighbors.filter(n => (n.name && n.name.trim()) || (n.phone && n.phone.trim())).length;
                      const emptyN = neighbors.length - filledN;
                      if (emptyN === 0) return null;
                      return (
                        <span className="text-[11px] text-[var(--ink-400)]">
                          {filledN} ingevuld · {emptyN} leeg ingeklapt
                        </span>
                      );
                    })()}
                  </div>
                  <div className="space-y-3">
                    {/* Filled rows (always expanded) */}
                    {neighbors.map((nbr, idx) => {
                      const filled = (nbr.name && nbr.name.trim()) || (nbr.phone && nbr.phone.trim());
                      if (!filled) return null;
                      return (
                        <NeighborRow key={`${nbr.address}-${idx}`}
                                     nbr={nbr} idx={idx}
                                     onSave={(patch) => updateNeighbor(idx, patch)}
                                     lookupUrl={klapperLookupUrl(nbr.address)} />
                      );
                    })}

                    {/* Empty rows — collapsed into a chip list, click to expand */}
                    <CollapsedEmptyRow neighbors={neighbors}
                                       onSave={updateNeighbor}
                                       klapperLookupUrl={klapperLookupUrl} />

                    {neighbors.length === 0 && (
                      <div className="text-[13px] italic text-[var(--ink-400)]">
                        Geen huisnummer kunnen afleiden uit het adres — geen
                        buren raster mogelijk.
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </section>

      {/* Google Business — placeholder */}
      <section className="card !p-0 overflow-hidden">
        <header className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-[var(--border-soft)]">
          <button type="button" onClick={() => setGoogleOpen(v => !v)}
                  className="flex items-start gap-2 text-left flex-1">
            {googleOpen ? <ChevronUp size={16} className="mt-1 text-[var(--ink-400)]" />
                        : <ChevronDown size={16} className="mt-1 text-[var(--ink-400)]" />}
            <div>
              <h3 className="font-display text-[20px] leading-tight">Google Business</h3>
              <p className="text-[12px] text-[var(--ink-500)] mt-0.5">
                Bedrijfsinformatie op {lead.address} — integratie volgt
              </p>
            </div>
          </button>
        </header>
        {googleOpen && (
          <div className="px-5 py-5 text-[13px] italic text-[var(--ink-400)]">
            Nog niet beschikbaar — koppeling met Google Business API komt eraan.
          </div>
        )}
      </section>
    </div>
  );
}

function SourceBadge({ source }) {
  if (!source) return null;
  const map = {
    Klapper: "bg-[#E5C9A0] text-[#5A3F0B] border-[#D8B883]",
    Manueel: "bg-[var(--teal-100,#D8EDEB)] text-[var(--teal-700)] border-[var(--teal-300,#86c9c2)]",
  };
  const cls = map[source] || "bg-[var(--sand-50)] text-[var(--ink-500)] border-[var(--border-soft)]";
  return (
    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${cls}`}>
      {source}
    </span>
  );
}

function CollapsedEmptyRow({ neighbors, onSave, klapperLookupUrl }) {
  const [expandedIdx, setExpandedIdx] = useState(null);
  const empties = neighbors
    .map((n, idx) => ({ ...n, _idx: idx }))
    .filter(n => !((n.name && n.name.trim()) || (n.phone && n.phone.trim())));
  if (empties.length === 0) return null;
  return (
    <div className="rounded-lg border border-dashed border-[var(--border-soft)] bg-[var(--sand-50)] p-3"
         data-testid="klapper-empty-collapsed">
      <div className="text-[11px] text-[var(--ink-500)] mb-2">
        Geen gegevens gevonden — klik op een nummer om manueel aan te vullen
      </div>
      <div className="flex flex-wrap gap-2">
        {empties.map(n => (
          <button key={n._idx} type="button"
                  data-testid={`empty-chip-${n._idx}`}
                  onClick={() => setExpandedIdx(expandedIdx === n._idx ? null : n._idx)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] font-medium border transition-colors ${
                    expandedIdx === n._idx
                      ? "bg-white border-[var(--teal-300,#86c9c2)] text-[var(--teal-800)]"
                      : "bg-white border-[var(--border-soft)] text-[var(--ink-700)] hover:border-[var(--teal-300,#86c9c2)]"
                  }`}>
            nr {n.number}
          </button>
        ))}
      </div>
      {expandedIdx !== null && (
        <div className="mt-3 pt-3 border-t border-[var(--border-soft)]">
          <NeighborRow nbr={neighbors[expandedIdx]} idx={expandedIdx}
                       onSave={(patch) => onSave(expandedIdx, patch)}
                       lookupUrl={klapperLookupUrl(neighbors[expandedIdx].address)} />
        </div>
      )}
    </div>
  );
}

function MainRow({ main, onSave }) {
  const [name, setName] = useState(main.name || "");
  const [phone, setPhone] = useState(main.phone || "");
  const [verified, setVerified] = useState(!!main.verified_owner);
  const [contacted, setContacted] = useState(!!main.contacted);
  const [note, setNote] = useState(main.note || "");

  useEffect(() => {
    setName(main.name || "");
    setPhone(main.phone || "");
    setVerified(!!main.verified_owner);
    setContacted(!!main.contacted);
    setNote(main.note || "");
  }, [main]);

  const filled = (name && name.trim()) || (phone && phone.trim());

  return (
    <div className={`rounded-lg border p-4 ${filled ? "bg-[var(--sand-50)] border-[var(--teal-300,#86c9c2)]" : "border-[var(--border-soft)]"}`}>
      <div className="grid grid-cols-12 gap-3 items-center">
        <div className="col-span-12 sm:col-span-5">
          <input value={name} placeholder="Naam eigenaar (Klapper)"
                 data-testid="klapper-main-name"
                 onChange={(e) => setName(e.target.value)}
                 onBlur={() => name !== (main.name || "") && onSave({ name: name.trim() || null })}
                 className="w-full h-10 px-3 rounded-lg border border-[var(--border-soft)] text-[13px]" />
        </div>
        <div className="col-span-12 sm:col-span-4">
          <input value={phone} placeholder="Telefoon"
                 data-testid="klapper-main-phone"
                 onChange={(e) => setPhone(e.target.value)}
                 onBlur={() => phone !== (main.phone || "") && onSave({ phone: phone.trim() || null })}
                 className="w-full h-10 px-3 rounded-lg border border-[var(--border-soft)] text-[13px]" />
        </div>
        <div className="col-span-6 sm:col-span-3 flex items-center gap-2 text-[12px]">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={verified}
                   data-testid="klapper-main-verified"
                   onChange={(e) => { setVerified(e.target.checked); onSave({ verified_owner: e.target.checked }); }}
                   className="accent-[var(--teal-600)]" />
            Geverifieerd eigenaar
          </label>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-3 items-center mt-2">
        <div className="col-span-12 sm:col-span-9">
          <input value={note} placeholder="Notitie (bv. bron, datum opzoeking, …)"
                 data-testid="klapper-main-note"
                 onChange={(e) => setNote(e.target.value)}
                 onBlur={() => note !== (main.note || "") && onSave({ note: note.trim() || null })}
                 className="w-full h-9 px-3 rounded-lg border border-[var(--border-soft)] text-[13px]" />
        </div>
        <div className="col-span-12 sm:col-span-3 flex items-center justify-end gap-2 text-[12px]">
          <SourceBadge source={main.source} />
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={contacted}
                   data-testid="klapper-main-contacted"
                   onChange={(e) => { setContacted(e.target.checked); onSave({ contacted: e.target.checked }); }}
                   className="accent-[var(--teal-600)]" />
            Gecontacteerd
          </label>
        </div>
      </div>
    </div>
  );
}

function NeighborRow({ nbr, idx, onSave, lookupUrl }) {
  const [name, setName] = useState(nbr.name || "");
  const [phone, setPhone] = useState(nbr.phone || "");
  const [contacted, setContacted] = useState(!!nbr.contacted);
  const [note, setNote] = useState(nbr.note || "");

  useEffect(() => {
    setName(nbr.name || "");
    setPhone(nbr.phone || "");
    setContacted(!!nbr.contacted);
    setNote(nbr.note || "");
  }, [nbr]);

  const filled = (name && name.trim()) || (phone && phone.trim());

  return (
    <div className={`rounded-lg border p-3 ${filled ? "bg-white border-[var(--teal-300,#86c9c2)]" : "border-[var(--border-soft)]"}`}
         data-testid={`neighbor-${idx}`}>
      <div className="grid grid-cols-12 gap-3 items-center">
        <div className="col-span-12 sm:col-span-2 flex items-center justify-between">
          <span className="font-medium tabular text-[13px]">nr {nbr.number}</span>
          <a href={lookupUrl} target="_blank" rel="noreferrer"
             title="Zoek dit adres op deklapper.be"
             className="text-[var(--ink-300)] hover:text-[#B8924A]">
            <ExternalLink size={12} />
          </a>
        </div>
        <div className="col-span-12 sm:col-span-4">
          <input value={name} placeholder="Naam (indien gevonden)"
                 data-testid={`neighbor-name-${idx}`}
                 onChange={(e) => setName(e.target.value)}
                 onBlur={() => name !== (nbr.name || "") && onSave({ name: name.trim() || null })}
                 className="w-full h-9 px-3 rounded-lg border border-[var(--border-soft)] text-[13px]" />
        </div>
        <div className="col-span-12 sm:col-span-3">
          <input value={phone} placeholder="Telefoon"
                 data-testid={`neighbor-phone-${idx}`}
                 onChange={(e) => setPhone(e.target.value)}
                 onBlur={() => phone !== (nbr.phone || "") && onSave({ phone: phone.trim() || null })}
                 className="w-full h-9 px-3 rounded-lg border border-[var(--border-soft)] text-[13px]" />
        </div>
        <div className="col-span-12 sm:col-span-3 flex items-center justify-end gap-2 text-[11px]">
          <SourceBadge source={nbr.source} />
          <label className="flex items-center gap-1.5 cursor-pointer text-[var(--ink-500)]">
            <input type="checkbox" checked={contacted}
                   data-testid={`neighbor-contacted-${idx}`}
                   onChange={(e) => { setContacted(e.target.checked); onSave({ contacted: e.target.checked }); }}
                   className="accent-[var(--teal-600)]" />
            Gecontacteerd
          </label>
          {filled && (
            <span className="inline-flex items-center gap-1 text-[var(--teal-700)]">
              <Check size={11} />
            </span>
          )}
        </div>
      </div>
      {(filled || note) && (
        <input value={note} placeholder="Notitie over deze buur…"
               data-testid={`neighbor-note-${idx}`}
               onChange={(e) => setNote(e.target.value)}
               onBlur={() => note !== (nbr.note || "") && onSave({ note: note.trim() || null })}
               className="mt-2 w-full h-8 px-3 rounded-lg border border-[var(--border-soft)] text-[12px]" />
      )}
    </div>
  );
}
